import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';
import { isCalendarYearRange, isFinancialYearRange, SystemMinimumDate } from 'src/common';
import { DatePickerNames } from 'src/common/components/DateRangePickerPreDefinedDates';
import { DateRange } from 'src/store';
import { MainDetails } from '../../common/store';
import { DefaultAvailablePeriods } from '../components/MultiplePeriodSelector';
import { updateReportParameters } from './helper';
import {
  downloadReport,
  fetchClientAccount,
  fetchReportParametersList,
  getCGTRecordsForClient,
  getReportParametersById,
  getReportStatus,
  getReportTemplate,
  initiateReportGeneration,
  saveAppendixFile,
  saveCoverPageFile,
} from './thunks';
import {
  CGTRecord,
  ClientNewReportsState,
  DateTypeExpensesReport,
  DateTypeIncomeReport,
  GroupByIncomeReport,
  GroupByPortfolioValuation,
  GroupBySecurityContribution,
  GroupBySecurityPerformance,
  GroupByTransactionHistory,
  GroupConsolidatedOption,
  HideByAssetClassPerformance,
  HideByPortfolioValuation,
  HideBySecurityContribution,
  HideByTransactionHistory,
  HidingOptionAssetAllocation,
  HidingOptionCurrencyExposure,
  HidingOptionHoldingsVsTargetVariance,
  HidingOptionPortfolioPerformance,
  HidingOptionSecurityPerformance,
  HidingOptionsRequest,
  InitiateReportRequest,
  InitiateReportRequestModel,
  PerformanceDataType,
  PerformanceMethodology,
  PeriodBaseRequest,
  RealisedCapitalGainsAndLossesOption,
  ReportFormat,
  ReportGeneration,
  ReportingParameter,
  ReportParameterTemplateViewModel,
  ReportToSort,
  SavedParameter,
  SaveReportingParametersRequest,
  ShowReceivablesAsType,
  SortByCashTransactions,
  SortByIncomeReport,
  SortByPortfolioValuation,
  SortByTransactionHistory,
  TempFileRef,
  UpdateGroupConsolidatedOption,
  YearType,
} from './types';

const dateTo = DateTime.now().toISODate();

export const initialState: ClientNewReportsState = {
  parameters: {
    clientName: '',
    clientSubTypeId: -1,
    inceptionDate: '',
    dates: { dateFrom: '', dateTo: dateTo },
    isDataFromTriumph: false,
    parameterName: '',
    mergeReports: false,
    reportsToSort: [],
    showItemOrdering: false,
    includeLogo: true,
    reportingParameter: {
      visibleToAfsl: false,
      parameterName: '',
    },
    expandedReports: false,
    includeCoverLetter: false,
    includeAppendix: false,
    coverLetter: undefined,
    appendix: undefined,
    reportFormat: ReportFormat.PDF,
    selectedReports: {
      projectedIncomeCashflow: {
        selected: false,
        asAtDateType: DatePickerNames.Today,
        asAtDate: dateTo,
        groupOption: GroupConsolidatedOption.UnderlyingClients,
        portfolioId: -1,
      },
      assetClassPerformance: {
        selected: false,
        dateRangeType: '',
        fromDate: '',
        toDate: dateTo,
        excludeFrankingCredits: false,
        showAnnualisedReturns: true,
        applyExclusionList: true,
        excludeCapitalIncomeReturns: false,
        includeAllocationChange: false,
        includeReturnsSummary: false,
        includeBenchmarkComparison: false,
        multiPeriods: [],
        performanceMethodology: PerformanceMethodology.TimeWeighted,
        groupOption: GroupConsolidatedOption.UnderlyingClients,
      },
      currencyExposure: {
        selected: false,
        asAtDateType: DatePickerNames.Today,
        asAtDate: dateTo,
        portfolioId: -1,
        applyExclusionList: true,
        groupOption: GroupConsolidatedOption.UnderlyingClients,
        includeDetailTable: false,
      },
      holdingsVsTargetVariance: {
        selected: false,
        asAtDateType: DatePickerNames.Today,
        asAtDate: dateTo,
        portfolioId: -1,
        includeSummary: false,
        applyExclusionList: true,
      },
      portfolioSnapshot: {
        selected: false,
        multiPeriods: [],
        dateRangeType: '',
        performanceDataType: PerformanceDataType.Daily,
        fromDate: '',
        toDate: dateTo,
        portfolioId: -1,
        benchmarkId: -1,
        primaryBenchmark: false,
        secondaryBenchmark: false,
        includeAfterFeesReturn: true,
        excludeFrankingCredits: true,
        performanceMethodology: PerformanceMethodology.TimeWeighted,
        groupOption: GroupConsolidatedOption.UnderlyingClients,
        applyExclusionList: true,
      },
      taxInvoice: {
        dateRangeType: '',
        fromDate: '',
        toDate: dateTo,
        selected: false,
      },
      portfolioPerformance: {
        selected: false,
        dateRangeType: '',
        fromDate: '',
        toDate: dateTo,
        multiPeriods: [],
        primaryBenchmark: false,
        secondaryBenchmark: false,
        excludeFrankingCredits: false,
        showAnnualisedReturns: true,
        applyExclusionList: true,
        includeAfterFeesReturn: true,
        includeNetContributions: true,
        benchmarkId: -1,
        performanceMethodology: PerformanceMethodology.TimeWeighted,
        groupOption: GroupConsolidatedOption.UnderlyingClients,
      },
      securityContribution: {
        selected: false,
        dateRangeType: '',
        fromDate: '',
        toDate: dateTo,
        groupBy: GroupBySecurityContribution.AssetClass,
        excludeFrankingCredits: false,
        showAnnualisedReturns: true,
        applyExclusionList: true,
        excludeCapitalIncomeReturns: true,
        includeDetailTable: true,
        performanceMethodology: PerformanceMethodology.TimeWeighted,
        groupOption: GroupConsolidatedOption.UnderlyingClients,
      },
      taxSummary: {
        selected: false,
        fromDate: DateTime.fromObject({ year: DateTime.now().year - 1, month: 7, day: 1 }).toISODate(),
        toDate: DateTime.fromObject({ year: DateTime.now().year, month: 6, day: 30 }).toISODate(),
        dateRangeType: DatePickerNames.LastFinancialYear,
        yearType: YearType.FinancialYear,
      },
      investmentIncomeTaxComponent: {
        selected: false,
        excludeFxGainsLosses: true,
        fromDate: DateTime.fromObject({ year: DateTime.now().year - 1, month: 7, day: 1 }).toISODate(),
        toDate: DateTime.fromObject({ year: DateTime.now().year, month: 6, day: 30 }).toISODate(),
        dateRangeType: DatePickerNames.LastFinancialYear,
        yearType: YearType.FinancialYear,
        version: 'Formatted',
      },
      incomeReport: {
        dateRangeType: '',
        fromDate: '',
        toDate: dateTo,
        selected: false,
        groupBy: GroupByIncomeReport.Account,
        sortBy: SortByIncomeReport.Security,
        dateType: DateTypeIncomeReport.ExDate,
        showReceivablesAs: ShowReceivablesAsType.UnmappedAccruals,
        excludeCash: false,
        showOutstandingPaymentOnly: false,
        groupOption: GroupConsolidatedOption.UnderlyingClients,
      },
      transactionHistory: {
        dateRangeType: '',
        fromDate: '',
        toDate: dateTo,
        selected: false,
        excludeCash: false,
        includeDistribution: false,
        excludeAssetDescription: true,
        excludeAccounts: true,
        includeBrokerage: true,
        includeGST: true,
        includeOtherCharges: false,
        sortBy: SortByTransactionHistory.SecurityName,
        groupBy: GroupByTransactionHistory.Account,
        groupOption: GroupConsolidatedOption.UnderlyingClients,
      },
      cashTransactions: {
        fromDate: '',
        toDate: dateTo,
        sortBy: SortByCashTransactions.DateAscending,
        selected: false,
        excludeAudValue: true,
        groupOption: GroupConsolidatedOption.UnderlyingClients,
      },
      expenses: {
        fromDate: '',
        toDate: dateTo,
        excludeExpenseType: false,
        dateType: DateTypeExpensesReport.TradeDate,
        selected: false,
        groupOption: GroupConsolidatedOption.UnderlyingClients,
      },
      unrealisedCapitalGainsAndLosses: {
        selected: false,
        asAtDate: dateTo,
        asAtDateType: DatePickerNames.Today,
        excludeFxGainsLosses: true,
        showCostBaseAdjustments: true,
      },
      realisedCapitalGainsAndLosses: {
        fromDate: '',
        toDate: dateTo,
        includeSummary: true,
        excludeFxGainsLosses: true,
        showCostBaseAdjustments: true,
        selected: false,
      },
      securityPerformance: {
        fromDate: '',
        toDate: dateTo,
        groupBy: GroupBySecurityPerformance.AssetClass,
        excludeFrankingCredits: false,
        showAnnualisedReturns: true,
        excludeCapitalIncomeReturns: false,
        excludeMarketFxReturn: false,
        applyExclusionList: true,
        selected: false,
        performanceMethodology: PerformanceMethodology.TimeWeighted,
        groupOption: GroupConsolidatedOption.UnderlyingClients,
      },
      portfolioValuation: {
        asAtDate: dateTo,
        asAtDateType: DatePickerNames.Today,
        selected: false,
        excludeSecurityType: true,
        excludeGainsLosses: false,
        excludeGICS: true,
        excludeCostBase: false,
        excludeMarket: true,
        excludeAvgUnitCost: false,
        excludeEstIncomeYield: false,
        excludeEstGrossIncomeYield: true,
        excludeFXGainsLosses: true,
        excludeLocalPriceValue: true,
        applyExclusionList: true,
        excludeInterestAccrual: true,
        excludePendingCashDetails: true,
        excludePriceDate: false,
        excludeTotalReturn: true,
        sortBy: SortByPortfolioValuation.SecurityName,
        groupBy: GroupByPortfolioValuation.AssetClass,
        portfolioId: -1,
        groupOption: GroupConsolidatedOption.UnderlyingClients,
      },
      assetAllocation: {
        asAtDate: dateTo,
        asAtDateType: DatePickerNames.Today,
        includeAssetCategory: true,
        includeDetailTable: false,
        applyExclusionList: true,
        selected: false,
        portfolioId: -1,
        groupOption: GroupConsolidatedOption.UnderlyingClients,
      },
      contributionsAndWithdrawals: {
        dateRangeType: '',
        fromDate: '',
        toDate: dateTo,
        selected: false,
        groupOption: GroupConsolidatedOption.UnderlyingClients,
      },
      unrealisedNonCGT: {
        selected: false,
        excludeFxGainsLosses: true,
        asAtDateType: DatePickerNames.Today,
        asAtDate: dateTo,
      },
      uXchangeTaxStatement: {
        selected: false,
        fromDate: DateTime.fromObject({ year: DateTime.now().year - 1, month: 7, day: 1 }).toISODate(),
        toDate: DateTime.fromObject({ year: DateTime.now().year, month: 6, day: 30 }).toISODate(),
        dateRangeType: DatePickerNames.LastFinancialYear,
      },
    },
    selectedParameter: undefined,
  },
  reportGeneration: {
    isGenerating: false,
    isExpired: false,
    requestId: '',
    downloadFileName: '',
    isDataFromTriumph: false,
    isMergeReport: false,
    key: '',
    clientId: 0,
    clientName: '',
    isGenerated: false,
    mergeReportOrder: [],
    dateRange: '',
    reportName: '',
  },
  savedParametersList: [],
  clientFinancialYears: [],
};

const blankcgtRecord: CGTRecord = {
  fyDescription: '',
  currentYear: false,
  statusDescription: '',
  strategyDescription: '',
  externalCarryForwardLoss: 0.0,
  platformCarryForwardLoss: 0.0,
  finalisedDate: undefined,
};

export const newReportsSlice = createSlice({
  name: '@@client/newReports',
  initialState,
  reducers: {
    resetReports: (state) => {
      const inceptionDate = SystemMinimumDate;
      state.parameters.mergeReports = false;
      state.parameters.includeLogo = true;
      state.parameters.coverLetter = '';
      state.parameters.includeCoverLetter = false;
      state.parameters.appendix = '';
      state.parameters.selectedParameter = undefined;
      state.parameters.includeAppendix = false;
      state.parameters.reportFormat = ReportFormat.PDF;
      state.parameters.reportingParameter = {
        parameterName: '',
        visibleToAfsl: false,
        afslId: -1,
        afslName: '',
      };
      state.parameters.reportsToSort = [];
      state.parameters.dates = {
        dateFrom: inceptionDate,
        dateTo: dateTo,
        dateRangeType: DatePickerNames.InceptionToDate,
      };
      state.parameters.selectedReports = {
        projectedIncomeCashflow: {
          selected: false,
          asAtDateType: DatePickerNames.Today,
          asAtDate: dateTo,
          groupOption: GroupConsolidatedOption.UnderlyingClients,
          portfolioId: -1,
        },
        currencyExposure: {
          selected: false,
          asAtDateType: DatePickerNames.Today,
          asAtDate: dateTo,
          groupOption: GroupConsolidatedOption.UnderlyingClients,
          portfolioId: -1,
          applyExclusionList: true,
          includeDetailTable: false,
        },
        assetClassPerformance: {
          selected: false,
          dateRangeType: DatePickerNames.InceptionToDate,
          fromDate: inceptionDate,
          toDate: dateTo,
          excludeFrankingCredits: false,
          showAnnualisedReturns: true,
          applyExclusionList: true,
          excludeCapitalIncomeReturns: false,
          includeAllocationChange: false,
          includeReturnsSummary: false,
          includeBenchmarkComparison: false,
          performanceMethodology: PerformanceMethodology.TimeWeighted,
          multiPeriods: DefaultAvailablePeriods.map((period) => {
            return { periodType: period.periodType.name, periodValue: period.periodValue } as PeriodBaseRequest;
          }),
          groupOption: GroupConsolidatedOption.UnderlyingClients,
        },
        holdingsVsTargetVariance: {
          selected: false,
          asAtDateType: DatePickerNames.Today,
          asAtDate: dateTo,
          portfolioId: -1,
          includeSummary: false,
          applyExclusionList: true,
        },
        portfolioSnapshot: {
          selected: false,
          multiPeriods: [],
          performanceDataType: PerformanceDataType.Daily,
          dateRangeType: DatePickerNames.InceptionToDate,
          fromDate: inceptionDate,
          toDate: dateTo,
          portfolioId: -1,
          benchmarkId: -1,
          primaryBenchmark: false,
          secondaryBenchmark: false,
          excludeFrankingCredits: true,
          includeAfterFeesReturn: true,
          performanceMethodology: PerformanceMethodology.TimeWeighted,
          groupOption: GroupConsolidatedOption.UnderlyingClients,
          applyExclusionList: true,
        },
        taxInvoice: {
          dateRangeType: DatePickerNames.InceptionToDate,
          fromDate: inceptionDate,
          toDate: dateTo,
          selected: false,
        },
        portfolioPerformance: {
          selected: false,
          multiPeriods: [],
          dateRangeType: DatePickerNames.InceptionToDate,
          fromDate: inceptionDate,
          toDate: dateTo,
          excludeFrankingCredits: false,
          showAnnualisedReturns: true,
          applyExclusionList: true,
          includeAfterFeesReturn: true,
          includeNetContributions: true,
          benchmarkId: -1,
          primaryBenchmark: false,
          secondaryBenchmark: false,
          performanceMethodology: PerformanceMethodology.TimeWeighted,
          groupOption: GroupConsolidatedOption.UnderlyingClients,
        },
        securityContribution: {
          selected: false,
          dateRangeType: DatePickerNames.InceptionToDate,
          fromDate: inceptionDate,
          toDate: dateTo,
          groupBy: GroupBySecurityContribution.AssetClass,
          excludeFrankingCredits: false,
          showAnnualisedReturns: true,
          applyExclusionList: true,
          excludeCapitalIncomeReturns: true,
          includeDetailTable: true,
          performanceMethodology: PerformanceMethodology.TimeWeighted,
          groupOption: GroupConsolidatedOption.UnderlyingClients,
        },
        taxSummary: {
          selected: false,
          fromDate: DateTime.fromObject({ year: DateTime.now().year - 1, month: 7, day: 1 }).toISODate(),
          toDate: DateTime.fromObject({ year: DateTime.now().year, month: 6, day: 30 }).toISODate(),
          dateRangeType: DatePickerNames.LastFinancialYear,
          yearType: YearType.FinancialYear,
        },
        investmentIncomeTaxComponent: {
          selected: false,
          excludeFxGainsLosses: true,
          fromDate: DateTime.fromObject({ year: DateTime.now().year - 1, month: 7, day: 1 }).toISODate(),
          toDate: DateTime.fromObject({ year: DateTime.now().year, month: 6, day: 30 }).toISODate(),
          dateRangeType: DatePickerNames.LastFinancialYear,
          yearType: YearType.FinancialYear,
          version: 'Formatted',
        },
        incomeReport: {
          dateRangeType: DatePickerNames.InceptionToDate,
          fromDate: inceptionDate,
          toDate: dateTo,
          selected: false,
          groupBy: GroupByIncomeReport.Account,
          sortBy: SortByIncomeReport.Security,
          dateType: DateTypeIncomeReport.ExDate,
          showReceivablesAs: ShowReceivablesAsType.UnmappedAccruals,
          excludeCash: false,
          showOutstandingPaymentOnly: false,
          groupOption: GroupConsolidatedOption.UnderlyingClients,
        },
        transactionHistory: {
          dateRangeType: DatePickerNames.InceptionToDate,
          fromDate: inceptionDate,
          toDate: dateTo,
          selected: false,
          excludeCash: false,
          includeDistribution: false,
          excludeAssetDescription: true,
          excludeAccounts: true,
          includeBrokerage: true,
          includeGST: true,
          includeOtherCharges: false,
          sortBy: SortByTransactionHistory.SecurityName,
          groupBy: GroupByTransactionHistory.Account,
          groupOption: GroupConsolidatedOption.UnderlyingClients,
        },
        cashTransactions: {
          dateRangeType: DatePickerNames.InceptionToDate,
          fromDate: inceptionDate,
          toDate: dateTo,
          sortBy: SortByCashTransactions.DateAscending,
          selected: false,
          excludeAudValue: true,
          groupOption: GroupConsolidatedOption.UnderlyingClients,
        },
        expenses: {
          dateRangeType: DatePickerNames.InceptionToDate,
          fromDate: inceptionDate,
          toDate: dateTo,
          excludeExpenseType: false,
          dateType: DateTypeExpensesReport.TradeDate,
          selected: false,
          groupOption: GroupConsolidatedOption.UnderlyingClients,
        },
        unrealisedCapitalGainsAndLosses: {
          selected: false,
          asAtDate: dateTo,
          asAtDateType: DatePickerNames.Today,
          excludeFxGainsLosses: true,
          showCostBaseAdjustments: true,
        },
        realisedCapitalGainsAndLosses: {
          dateRangeType: DatePickerNames.InceptionToDate,
          fromDate: inceptionDate,
          toDate: dateTo,
          includeSummary: true,
          excludeFxGainsLosses: true,
          showCostBaseAdjustments: true,
          selected: false,
        },
        securityPerformance: {
          dateRangeType: DatePickerNames.InceptionToDate,
          fromDate: inceptionDate,
          toDate: dateTo,
          groupBy: GroupBySecurityPerformance.AssetClass,
          excludeFrankingCredits: false,
          showAnnualisedReturns: true,
          excludeCapitalIncomeReturns: false,
          excludeMarketFxReturn: false,
          applyExclusionList: true,
          selected: false,
          performanceMethodology: PerformanceMethodology.TimeWeighted,
          groupOption: GroupConsolidatedOption.UnderlyingClients,
        },
        portfolioValuation: {
          asAtDate: dateTo,
          asAtDateType: DatePickerNames.Today,
          selected: false,
          excludeSecurityType: true,
          excludeGainsLosses: false,
          excludeGICS: true,
          excludeCostBase: false,
          excludeMarket: true,
          excludeAvgUnitCost: false,
          excludeEstIncomeYield: false,
          excludeEstGrossIncomeYield: true,
          excludeFXGainsLosses: true,
          excludeLocalPriceValue: true,
          applyExclusionList: true,
          excludeInterestAccrual: true,
          excludePendingCashDetails: true,
          excludePriceDate: false,
          excludeTotalReturn: true,
          sortBy: SortByPortfolioValuation.SecurityName,
          groupBy: GroupByPortfolioValuation.AssetClass,
          portfolioId: -1,
          groupOption: GroupConsolidatedOption.UnderlyingClients,
        },
        assetAllocation: {
          asAtDate: dateTo,
          asAtDateType: DatePickerNames.Today,
          includeAssetCategory: true,
          includeDetailTable: false,
          applyExclusionList: true,
          selected: false,
          portfolioId: -1,
          groupOption: GroupConsolidatedOption.UnderlyingClients,
        },
        contributionsAndWithdrawals: {
          dateRangeType: DatePickerNames.InceptionToDate,
          fromDate: inceptionDate,
          toDate: dateTo,
          selected: false,
          groupOption: GroupConsolidatedOption.UnderlyingClients,
        },
        unrealisedNonCGT: {
          selected: false,
          excludeFxGainsLosses: true,
          asAtDateType: DatePickerNames.Today,
          asAtDate: dateTo,
        },
        uXchangeTaxStatement: {
          selected: false,
          fromDate: DateTime.fromObject({ year: DateTime.now().year - 1, month: 7, day: 1 }).toISODate(),
          toDate: DateTime.fromObject({ year: DateTime.now().year, month: 6, day: 30 }).toISODate(),
          dateRangeType: DatePickerNames.LastFinancialYear,
        },
      };
    },
    setInceptionDate: (state, action: PayloadAction<string>) => {
      state.parameters.inceptionDate = action.payload;
    },
    setReportParameters: (state, action: PayloadAction<SaveReportingParametersRequest>) => {
      updateReportParameters(state, action);
    },
    setDateParameter: (state, action: PayloadAction<DateRange>) => {
      const dateRangeType = action.payload.dateRangeType ?? '';
      const isAsDateTypeValid =
        dateRangeType === DatePickerNames.Today ||
        dateRangeType === DatePickerNames.LastQuarter ||
        dateRangeType === DatePickerNames.LastFinancialYear ||
        dateRangeType === DatePickerNames.LastYear;
      state.parameters.dates = action.payload;
      state.parameters.selectedReports.portfolioPerformance.fromDate = action.payload.dateFrom;
      state.parameters.selectedReports.portfolioPerformance.toDate = action.payload.dateTo;
      state.parameters.selectedReports.portfolioPerformance.dateRangeType = action.payload.dateRangeType;
      state.parameters.selectedReports.portfolioSnapshot.fromDate = action.payload.dateFrom;
      state.parameters.selectedReports.portfolioSnapshot.toDate = action.payload.dateTo;
      state.parameters.selectedReports.portfolioSnapshot.dateRangeType = action.payload.dateRangeType;
      state.parameters.selectedReports.assetAllocation.asAtDate = action.payload.dateTo;
      state.parameters.selectedReports.projectedIncomeCashflow.asAtDate = action.payload.dateTo;
      state.parameters.selectedReports.unrealisedCapitalGainsAndLosses.asAtDate = action.payload.dateTo;
      state.parameters.selectedReports.portfolioValuation.asAtDate = action.payload.dateTo;
      state.parameters.selectedReports.incomeReport.fromDate = action.payload.dateFrom;
      state.parameters.selectedReports.incomeReport.toDate = action.payload.dateTo;
      state.parameters.selectedReports.incomeReport.dateRangeType = action.payload.dateRangeType;
      state.parameters.selectedReports.cashTransactions.fromDate = action.payload.dateFrom;
      state.parameters.selectedReports.cashTransactions.toDate = action.payload.dateTo;
      state.parameters.selectedReports.cashTransactions.dateRangeType = action.payload.dateRangeType;
      state.parameters.selectedReports.expenses.fromDate = action.payload.dateFrom;
      state.parameters.selectedReports.expenses.toDate = action.payload.dateTo;
      state.parameters.selectedReports.expenses.dateRangeType = action.payload.dateRangeType;
      state.parameters.selectedReports.realisedCapitalGainsAndLosses.fromDate = action.payload.dateFrom;
      state.parameters.selectedReports.realisedCapitalGainsAndLosses.toDate = action.payload.dateTo;
      state.parameters.selectedReports.realisedCapitalGainsAndLosses.dateRangeType = action.payload.dateRangeType;
      state.parameters.selectedReports.securityPerformance.fromDate = action.payload.dateFrom;
      state.parameters.selectedReports.securityPerformance.toDate = action.payload.dateTo;
      state.parameters.selectedReports.securityPerformance.dateRangeType = action.payload.dateRangeType;
      state.parameters.selectedReports.transactionHistory.fromDate = action.payload.dateFrom;
      state.parameters.selectedReports.transactionHistory.toDate = action.payload.dateTo;
      state.parameters.selectedReports.transactionHistory.dateRangeType = action.payload.dateRangeType;
      state.parameters.selectedReports.contributionsAndWithdrawals.fromDate = action.payload.dateFrom;
      state.parameters.selectedReports.contributionsAndWithdrawals.toDate = action.payload.dateTo;
      state.parameters.selectedReports.contributionsAndWithdrawals.dateRangeType = action.payload.dateRangeType;
      state.parameters.selectedReports.securityContribution.fromDate = action.payload.dateFrom;
      state.parameters.selectedReports.securityContribution.toDate = action.payload.dateTo;
      state.parameters.selectedReports.securityContribution.dateRangeType = action.payload.dateRangeType;
      state.parameters.selectedReports.holdingsVsTargetVariance.asAtDate = action.payload.dateTo;
      state.parameters.selectedReports.currencyExposure.asAtDate = action.payload.dateTo;
      state.parameters.selectedReports.assetClassPerformance.fromDate = action.payload.dateFrom;
      state.parameters.selectedReports.assetClassPerformance.toDate = action.payload.dateTo;
      state.parameters.selectedReports.assetClassPerformance.dateRangeType = action.payload.dateRangeType;
      state.parameters.selectedReports.unrealisedNonCGT.asAtDate = action.payload.dateTo;
      state.parameters.selectedReports.taxInvoice.fromDate = action.payload.dateFrom;
      state.parameters.selectedReports.taxInvoice.toDate = action.payload.dateTo;
      state.parameters.selectedReports.taxInvoice.dateRangeType = action.payload.dateRangeType;

      const dateFrom = DateTime.fromISO(action.payload.dateFrom);
      const dateTo = DateTime.fromISO(action.payload.dateTo);
      const isCalendarType = isCalendarYearRange(dateFrom, dateTo);
      const isFinancialType = isFinancialYearRange(dateFrom, dateTo);

      let yearType: YearType | undefined = undefined;

      if (dateRangeType === DatePickerNames.LastFinancialYear || dateRangeType === DatePickerNames.ThisFinancialYear || isFinancialType) {
        yearType = YearType.FinancialYear;
        state.parameters.selectedReports.uXchangeTaxStatement.fromDate = action.payload.dateFrom;
        state.parameters.selectedReports.uXchangeTaxStatement.toDate = action.payload.dateTo;
      } else if (dateRangeType === DatePickerNames.ThisYear || dateRangeType === DatePickerNames.LastYear || isCalendarType) {
        yearType = YearType.CalendarYear;
        state.parameters.selectedReports.taxSummary.yearType = YearType.CalendarYear;
      }
      if (yearType) {
        state.parameters.selectedReports.investmentIncomeTaxComponent.fromDate = action.payload.dateFrom;
        state.parameters.selectedReports.investmentIncomeTaxComponent.toDate = action.payload.dateTo;
        state.parameters.selectedReports.investmentIncomeTaxComponent.yearType = yearType;
        state.parameters.selectedReports.taxSummary.fromDate = action.payload.dateFrom;
        state.parameters.selectedReports.taxSummary.toDate = action.payload.dateTo;
        state.parameters.selectedReports.taxSummary.yearType = yearType;
      }

      const asAtDateType = DateTime.fromISO(action.payload.dateTo).hasSame(DateTime.now(), 'day')
        ? DatePickerNames.Today
        : isAsDateTypeValid
        ? action.payload.dateRangeType
        : 'Pre Defined Date';
      state.parameters.selectedReports.unrealisedCapitalGainsAndLosses.asAtDateType = asAtDateType;
      state.parameters.selectedReports.assetAllocation.asAtDateType = asAtDateType;
      state.parameters.selectedReports.portfolioValuation.asAtDateType = asAtDateType;
      state.parameters.selectedReports.holdingsVsTargetVariance.asAtDateType = asAtDateType;
      state.parameters.selectedReports.currencyExposure.asAtDateType = asAtDateType;
      state.parameters.selectedReports.unrealisedNonCGT.asAtDateType = asAtDateType;
      state.parameters.selectedReports.projectedIncomeCashflow.asAtDateType = asAtDateType;
    },
    expandAll: (state) => {
      state.parameters.expandedReports = !state.parameters.expandedReports;
      state.parameters.selectedReports.assetAllocation.expanded = state.parameters.expandedReports;
      state.parameters.selectedReports.assetClassPerformance.expanded = state.parameters.expandedReports;
      state.parameters.selectedReports.holdingsVsTargetVariance.expanded = state.parameters.expandedReports;
      state.parameters.selectedReports.cashTransactions.expanded = state.parameters.expandedReports;
      state.parameters.selectedReports.expenses.expanded = state.parameters.expandedReports;
      state.parameters.selectedReports.incomeReport.expanded = state.parameters.expandedReports;
      state.parameters.selectedReports.securityPerformance.expanded = state.parameters.expandedReports;
      state.parameters.selectedReports.securityContribution.expanded = state.parameters.expandedReports;
      state.parameters.selectedReports.portfolioSnapshot.expanded = state.parameters.expandedReports;
      state.parameters.selectedReports.portfolioPerformance.expanded = state.parameters.expandedReports;
      state.parameters.selectedReports.portfolioValuation.expanded = state.parameters.expandedReports;
      state.parameters.selectedReports.realisedCapitalGainsAndLosses.expanded = state.parameters.expandedReports;
      state.parameters.selectedReports.transactionHistory.expanded = state.parameters.expandedReports;
      state.parameters.selectedReports.contributionsAndWithdrawals.expanded = state.parameters.expandedReports;
      state.parameters.selectedReports.unrealisedCapitalGainsAndLosses.expanded = state.parameters.expandedReports;
      state.parameters.selectedReports.unrealisedNonCGT.expanded = state.parameters.expandedReports;
      state.parameters.selectedReports.investmentIncomeTaxComponent.expanded = state.parameters.expandedReports;
      state.parameters.selectedReports.currencyExposure.expanded = state.parameters.expandedReports;
      state.parameters.selectedReports.projectedIncomeCashflow.expanded = state.parameters.expandedReports;
      state.parameters.selectedReports.taxSummary.expanded = state.parameters.expandedReports;
    },
    expandContributionsAndWithdrawals: (state) => {
      state.parameters.selectedReports.contributionsAndWithdrawals.expanded = !state.parameters.selectedReports.contributionsAndWithdrawals.expanded;
    },
    expandProjectedIncomeCashflow: (state) => {
      state.parameters.selectedReports.projectedIncomeCashflow.expanded = !state.parameters.selectedReports.projectedIncomeCashflow.expanded;
    },
    expandAssetAllocation: (state) => {
      state.parameters.selectedReports.assetAllocation.expanded = !state.parameters.selectedReports.assetAllocation.expanded;
    },
    expandAssetClassPerformance: (state) => {
      state.parameters.selectedReports.assetClassPerformance.expanded = !state.parameters.selectedReports.assetClassPerformance.expanded;
    },
    expandHoldingsVsTargetVariance: (state) => {
      state.parameters.selectedReports.holdingsVsTargetVariance.expanded = !state.parameters.selectedReports.holdingsVsTargetVariance.expanded;
    },
    expandCurrencyExposure: (state) => {
      state.parameters.selectedReports.currencyExposure.expanded = !state.parameters.selectedReports.currencyExposure.expanded;
    },
    expandCashTransactions: (state) => {
      state.parameters.selectedReports.cashTransactions.expanded = !state.parameters.selectedReports.cashTransactions.expanded;
    },
    expandExpenses: (state) => {
      state.parameters.selectedReports.expenses.expanded = !state.parameters.selectedReports.expenses.expanded;
    },
    expandIncomeReport: (state) => {
      state.parameters.selectedReports.incomeReport.expanded = !state.parameters.selectedReports.incomeReport.expanded;
    },
    expandUnrealisedNonCgt: (state) => {
      state.parameters.selectedReports.unrealisedNonCGT.expanded = !state.parameters.selectedReports.unrealisedNonCGT.expanded;
    },
    expandTaxSummary: (state) => {
      state.parameters.selectedReports.taxSummary.expanded = !state.parameters.selectedReports.taxSummary.expanded;
    },
    expandInvestmentIncomeTax: (state) => {
      state.parameters.selectedReports.investmentIncomeTaxComponent.expanded =
        !state.parameters.selectedReports.investmentIncomeTaxComponent.expanded;
    },
    expandSecurityPerformance: (state) => {
      state.parameters.selectedReports.securityPerformance.expanded = !state.parameters.selectedReports.securityPerformance.expanded;
    },
    expandPortfolioSnapshot: (state) => {
      state.parameters.selectedReports.portfolioSnapshot.expanded = !state.parameters.selectedReports.portfolioSnapshot.expanded;
    },
    expandPortfolioPerformance: (state) => {
      state.parameters.selectedReports.portfolioPerformance.expanded = !state.parameters.selectedReports.portfolioPerformance.expanded;
    },
    expandPortfolioValuation: (state) => {
      state.parameters.selectedReports.portfolioValuation.expanded = !state.parameters.selectedReports.portfolioValuation.expanded;
    },
    expandGainsAndLossesRealised: (state) => {
      state.parameters.selectedReports.realisedCapitalGainsAndLosses.expanded =
        !state.parameters.selectedReports.realisedCapitalGainsAndLosses.expanded;
    },
    expandGainsAndLossesUnrealised: (state) => {
      state.parameters.selectedReports.unrealisedCapitalGainsAndLosses.expanded =
        !state.parameters.selectedReports.unrealisedCapitalGainsAndLosses.expanded;
    },
    expandSecurityContribution: (state) => {
      state.parameters.selectedReports.securityContribution.expanded = !state.parameters.selectedReports.securityContribution.expanded;
    },
    expandTransactionHistory: (state) => {
      state.parameters.selectedReports.transactionHistory.expanded = !state.parameters.selectedReports.transactionHistory.expanded;
    },
    setUseTriumphAsDataSourceParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.isDataFromTriumph = action.payload;
    },
    setMergeReportsParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.mergeReports = action.payload;
    },
    setIncludeLogoParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.includeLogo = action.payload;
    },
    setIncludeCoverLetterParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.includeCoverLetter = action.payload;
      if (!action.payload) state.parameters.coverLetter = undefined;
    },
    setIncludeAppendixParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.includeAppendix = action.payload;
      if (!action.payload) state.parameters.appendix = undefined;
    },
    setCashTransactionsSortByParameter: (state, action: PayloadAction<SortByCashTransactions>) => {
      state.parameters.selectedReports.cashTransactions.sortBy = action.payload;
    },
    setCashTransactionsSelectedParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.cashTransactions.selected = action.payload;
    },
    setUnrealisedCapitalAndGainsExcludeFxParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.unrealisedCapitalGainsAndLosses.excludeFxGainsLosses = action.payload;
    },
    setUnrealisedCapitalAndGainsShowCostBaseAdjustmentsParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.unrealisedCapitalGainsAndLosses.showCostBaseAdjustments = action.payload;
    },
    setUnrealisedNonCgtExcludeFxParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.unrealisedNonCGT.excludeFxGainsLosses = action.payload;
    },
    setInvestmentIncomeTaxExcludeFxParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.investmentIncomeTaxComponent.excludeFxGainsLosses = action.payload;
    },
    setInvestmentIncomeTaxVersionParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.investmentIncomeTaxComponent.version = action.payload ? 'Flattened' : 'Formatted';
    },
    setIncomeShowReceivablesAsTypeParameter: (state, action: PayloadAction<ShowReceivablesAsType>) => {
      state.parameters.selectedReports.incomeReport.showReceivablesAs = action.payload;
    },
    setRealisedCapitalAndGainsOptionParameter: (state, action: PayloadAction<HidingOptionsRequest<RealisedCapitalGainsAndLossesOption>>) => {
      state.parameters.selectedReports.realisedCapitalGainsAndLosses[action.payload.option] = action.payload.value;
    },
    setUXChangeTaxStatementSelectedParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.uXchangeTaxStatement.selected = action.payload;
    },
    setUnrealisedNonCGTSelectedParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.unrealisedNonCGT.selected = action.payload;
    },
    setAssetClassPerformanceSelectedParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.assetClassPerformance.selected = action.payload;
    },
    setAssetClassPerformanceMethodology: (state, action: PayloadAction<PerformanceMethodology>) => {
      state.parameters.selectedReports.assetClassPerformance.performanceMethodology = action.payload;
    },
    setPortfolioSnapshotIncludeAfterFeesReturn: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.portfolioSnapshot.includeAfterFeesReturn = action.payload;
    },
    setPortfolioSnapshotExcludeFrankingCredits: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.portfolioSnapshot.excludeFrankingCredits = action.payload;
    },
    setPortfolioSnapshotPrimaryBenchmark: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.portfolioSnapshot.primaryBenchmark = action.payload;
    },
    setPortfolioSnapshotSecondaryBenchmark: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.portfolioSnapshot.secondaryBenchmark = action.payload;
    },
    setExpensesSelectedParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.expenses.selected = action.payload;
    },
    setExpensesExcludeExpenseTypeParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.expenses.excludeExpenseType = action.payload;
    },
    setExpensesReportDateTypeParameter: (state, action: PayloadAction<DateTypeExpensesReport>) => {
      state.parameters.selectedReports.expenses.dateType = action.payload;
    },
    setCapitalGainsLossesUnrealisedSelectedParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.unrealisedCapitalGainsAndLosses.selected = action.payload;
    },
    setRealisedCapitalGainsAndLossesSelectedParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.realisedCapitalGainsAndLosses.selected = action.payload;
    },
    setSecurityPerformanceSelectedParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.securityPerformance.selected = action.payload;
    },
    setTransactionHistorySelectedParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.transactionHistory.selected = action.payload;
    },
    setTransactionHistoryHidingOptionsParameter: (state, action: PayloadAction<HidingOptionsRequest<HideByTransactionHistory>>) => {
      state.parameters.selectedReports.transactionHistory[action.payload.option] = action.payload.value;
    },
    setAssetClassPerformanceHidingOptionParameter: (state, action: PayloadAction<HidingOptionsRequest<HideByAssetClassPerformance>>) => {
      state.parameters.selectedReports.assetClassPerformance[action.payload.option] = action.payload.value;
    },
    setTransactionHistoryGroupByParameter: (state, action: PayloadAction<GroupByTransactionHistory>) => {
      state.parameters.selectedReports.transactionHistory.groupBy = action.payload;
    },
    setTransactionHistorySortByParameter: (state, action: PayloadAction<SortByTransactionHistory>) => {
      state.parameters.selectedReports.transactionHistory.sortBy = action.payload;
    },
    setIncomeReportSelectedParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.incomeReport.selected = action.payload;
    },
    setIncomeReportExcludeCashFilterOptionsParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.incomeReport.excludeCash = action.payload;
    },
    setIncomeReportOutstandingPaymentsOnlyFilterOptionsParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.incomeReport.showOutstandingPaymentOnly = action.payload;
    },
    setIncomeReportGroupByParameter: (state, action: PayloadAction<GroupByIncomeReport>) => {
      state.parameters.selectedReports.incomeReport.groupBy = action.payload;
    },
    setIncomeReportSortByParameter: (state, action: PayloadAction<SortByIncomeReport>) => {
      state.parameters.selectedReports.incomeReport.sortBy = action.payload;
    },
    setIncomeReportDateTypeParameter: (state, action: PayloadAction<DateTypeIncomeReport>) => {
      state.parameters.selectedReports.incomeReport.dateType = action.payload;
    },
    setSecurityPerformanceGroupByParameter: (state, action: PayloadAction<GroupBySecurityPerformance>) => {
      state.parameters.selectedReports.securityPerformance.groupBy = action.payload;
    },
    setSecurityPerformanceMethodology: (state, action: PayloadAction<PerformanceMethodology>) => {
      state.parameters.selectedReports.securityPerformance.performanceMethodology = action.payload;
    },
    setPortfolioValuationSelectedParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.portfolioValuation.selected = action.payload;
    },
    setPortfolioValuationSortByParameter: (state, action: PayloadAction<SortByPortfolioValuation>) => {
      state.parameters.selectedReports.portfolioValuation.sortBy = action.payload;
      if (action.payload === SortByPortfolioValuation.Gics) {
        state.parameters.selectedReports.portfolioValuation.excludeGICS = false;
      } else if (action.payload === SortByPortfolioValuation.SecurityType) {
        state.parameters.selectedReports.portfolioValuation.excludeSecurityType = false;
      }
    },
    setPortfolioValuationGroupByParameter: (state, action: PayloadAction<GroupByPortfolioValuation>) => {
      state.parameters.selectedReports.portfolioValuation.groupBy = action.payload;
      if (action.payload === GroupByPortfolioValuation.Gics) {
        state.parameters.selectedReports.portfolioValuation.excludeGICS = false;
      }
    },
    setSecurityContributionGroupByParamater: (state, action: PayloadAction<GroupBySecurityContribution>) => {
      state.parameters.selectedReports.securityContribution.groupBy = action.payload;
    },
    setSecurityContributionHidingOptionParameter: (state, action: PayloadAction<HidingOptionsRequest<HideBySecurityContribution>>) => {
      state.parameters.selectedReports.securityContribution[action.payload.option] = action.payload.value;
    },
    setSecurityContributionPerformanceMethodology: (state, action: PayloadAction<PerformanceMethodology>) => {
      state.parameters.selectedReports.securityContribution.performanceMethodology = action.payload;
    },
    setPortfolioValuationHidingOptionParameter: (state, action: PayloadAction<HidingOptionsRequest<HideByPortfolioValuation>>) => {
      state.parameters.selectedReports.portfolioValuation[action.payload.option] = action.payload.value;
    },
    setProjectedIncomeCashflowSelectedParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.projectedIncomeCashflow.selected = action.payload;
    },
    setAssetAllocationSelectedParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.assetAllocation.selected = action.payload;
    },
    setHoldingsVsTargetVarianceSelectedParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.holdingsVsTargetVariance.selected = action.payload;
    },
    setCurrencyExposureSelectedParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.currencyExposure.selected = action.payload;
    },
    setTaxSummarySelectedParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.taxSummary.selected = action.payload;
    },
    setSecurityContributionSelectedParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.securityContribution.selected = action.payload;
    },
    setPortfolioSnapshotMultiplePeriods: (state, action: PayloadAction<PeriodBaseRequest[]>) => {
      state.parameters.selectedReports.portfolioSnapshot.multiPeriods = action.payload;
    },
    setPortfolioPerformancePrimaryBenchmark: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.portfolioPerformance.primaryBenchmark = action.payload;
    },
    setPortfolioPerformanceSecondaryBenchmark: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.portfolioPerformance.secondaryBenchmark = action.payload;
    },
    setPortfolioPerformanceMultiplePeriods: (state, action: PayloadAction<PeriodBaseRequest[]>) => {
      state.parameters.selectedReports.portfolioPerformance.multiPeriods = action.payload;
    },
    setAssetClassPerformanceMultiplePeriods: (state, action: PayloadAction<PeriodBaseRequest[]>) => {
      state.parameters.selectedReports.assetClassPerformance.multiPeriods = action.payload;
    },
    setPortfolioSnapshotSelectedParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.portfolioSnapshot.selected = action.payload;
    },
    setPortfolioSnapshotPerformanceDataTypeParameter: (state, action: PayloadAction<PerformanceDataType>) => {
      state.parameters.selectedReports.portfolioSnapshot.performanceDataType = action.payload;
    },
    setTaxInvoiceSelectedParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.taxInvoice.selected = action.payload;
    },
    setPortfolioPerformanceSelectedParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.portfolioPerformance.selected = action.payload;
    },
    setInvestmentIncomeTaxSelectedParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.investmentIncomeTaxComponent.selected = action.payload;
    },
    setDateRangeAssetClassPerformanceParameter: (state, action: PayloadAction<DateRange>) => {
      state.parameters.selectedReports.assetClassPerformance.dateRangeType = action.payload.dateRangeType;
      state.parameters.selectedReports.assetClassPerformance.toDate = action.payload.dateTo;
      state.parameters.selectedReports.assetClassPerformance.fromDate = action.payload.dateFrom;
    },
    setDateRangePortfolioSnapshotParameter: (state, action: PayloadAction<DateRange>) => {
      state.parameters.selectedReports.portfolioSnapshot.dateRangeType = action.payload.dateRangeType;
      state.parameters.selectedReports.portfolioSnapshot.fromDate = action.payload.dateFrom;
      state.parameters.selectedReports.portfolioSnapshot.toDate = action.payload.dateTo;
    },
    setDateRangeSecurityContributionParameter: (state, action: PayloadAction<DateRange>) => {
      state.parameters.selectedReports.securityContribution.dateRangeType = action.payload.dateRangeType;
      state.parameters.selectedReports.securityContribution.toDate = action.payload.dateTo;
      state.parameters.selectedReports.securityContribution.fromDate = action.payload.dateFrom;
    },
    setDateRangeTaxSummaryParameter: (state, action: PayloadAction<DateRange>) => {
      state.parameters.selectedReports.taxSummary.dateRangeType = action.payload.dateRangeType;
      state.parameters.selectedReports.taxSummary.toDate = action.payload.dateTo;
      state.parameters.selectedReports.taxSummary.fromDate = action.payload.dateFrom;
    },
    setPortfolioSnapshotApplyExclusionList: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.portfolioSnapshot.applyExclusionList = action.payload;
    },
    setDateRangeTaxInvoiceParameter: (state, action: PayloadAction<DateRange>) => {
      state.parameters.selectedReports.taxInvoice.dateRangeType = action.payload.dateRangeType;
      state.parameters.selectedReports.taxInvoice.toDate = action.payload.dateTo;
      state.parameters.selectedReports.taxInvoice.fromDate = action.payload.dateFrom;
    },
    setDateRangePortfolioPerformanceParameter: (state, action: PayloadAction<DateRange>) => {
      state.parameters.selectedReports.portfolioPerformance.dateRangeType = action.payload.dateRangeType;
      state.parameters.selectedReports.portfolioPerformance.toDate = action.payload.dateTo;
      state.parameters.selectedReports.portfolioPerformance.fromDate = action.payload.dateFrom;
    },
    setDateRangeInvestmentIncomeTaxParameter: (state, action: PayloadAction<DateRange>) => {
      state.parameters.selectedReports.investmentIncomeTaxComponent.dateRangeType = action.payload.dateRangeType;
      state.parameters.selectedReports.investmentIncomeTaxComponent.toDate = action.payload.dateTo;
      state.parameters.selectedReports.investmentIncomeTaxComponent.fromDate = action.payload.dateFrom;
    },
    setInvestmentIncomeTaxYearTypeParameter: (state, action: PayloadAction<YearType>) => {
      state.parameters.selectedReports.investmentIncomeTaxComponent.yearType = action.payload;
    },
    setTaxSummaryYearTypeParameter: (state, action: PayloadAction<YearType>) => {
      state.parameters.selectedReports.taxSummary.yearType = action.payload;
    },
    setContributionsAndWithdrawalsSelectedParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.contributionsAndWithdrawals.selected = action.payload;
    },
    setAssetAllocationPortfolioParameter: (state, action: PayloadAction<number>) => {
      state.parameters.selectedReports.assetAllocation.portfolioId = action.payload;
    },
    setHoldingsVsTargetPortfolioParameter: (state, action: PayloadAction<number>) => {
      state.parameters.selectedReports.holdingsVsTargetVariance.portfolioId = action.payload;
    },
    setCurrencyExposurePortfolioParameter: (state, action: PayloadAction<number>) => {
      state.parameters.selectedReports.currencyExposure.portfolioId = action.payload;
    },
    setPortfolioSnapshotPortfolioParameter: (state, action: PayloadAction<number>) => {
      state.parameters.selectedReports.portfolioSnapshot.portfolioId = action.payload;
    },
    setPortfolioSnapshotBenchmarkParameter: (state, action: PayloadAction<number>) => {
      state.parameters.selectedReports.portfolioSnapshot.benchmarkId = action.payload;
    },
    setPortfolioSnapshotPerformanceMethodology: (state, action: PayloadAction<PerformanceMethodology>) => {
      state.parameters.selectedReports.portfolioSnapshot.performanceMethodology = action.payload;
    },
    setPortfolioPerformanceBenchmarkParameter: (state, action: PayloadAction<number>) => {
      state.parameters.selectedReports.portfolioPerformance.benchmarkId = action.payload;
    },
    setPortfolioPerformanceMethodology: (state, action: PayloadAction<PerformanceMethodology>) => {
      state.parameters.selectedReports.portfolioPerformance.performanceMethodology = action.payload;
    },
    setPortfolioValuationPortfolioIdParameter: (state, action: PayloadAction<number>) => {
      state.parameters.selectedReports.portfolioValuation.portfolioId = action.payload;
    },
    setProjectedIncomeCashflowPortfolioIdParameter: (state, action: PayloadAction<number>) => {
      state.parameters.selectedReports.projectedIncomeCashflow.portfolioId = action.payload;
    },
    setSelectedReportOrderParameter: (state, action: PayloadAction<ReportToSort[]>) => {
      state.parameters.reportsToSort = action.payload;
    },
    setShowItemOderingModalParameter: (state, action: PayloadAction<boolean>) => {
      state.parameters.showItemOrdering = action.payload;
    },
    setDateRangeUXChangeTaxStatementParameter: (state, action: PayloadAction<DateRange>) => {
      state.parameters.selectedReports.uXchangeTaxStatement.dateRangeType = action.payload.dateRangeType;
      state.parameters.selectedReports.uXchangeTaxStatement.fromDate = action.payload.dateFrom;
      state.parameters.selectedReports.uXchangeTaxStatement.toDate = action.payload.dateTo;
    },
    setAsAtDateProjectedIncomeCashflowParameter: (state, action: PayloadAction<DateRange>) => {
      state.parameters.selectedReports.projectedIncomeCashflow.asAtDateType = action.payload.dateRangeType;
      state.parameters.selectedReports.projectedIncomeCashflow.asAtDate = action.payload.dateTo;
    },
    setAsAtDateAssetAllocationParameter: (state, action: PayloadAction<DateRange>) => {
      state.parameters.selectedReports.assetAllocation.asAtDateType = action.payload.dateRangeType;
      state.parameters.selectedReports.assetAllocation.asAtDate = action.payload.dateTo;
    },
    setAsAtDateHoldingsVsTargetVarianceParameter: (state, action: PayloadAction<DateRange>) => {
      state.parameters.selectedReports.holdingsVsTargetVariance.asAtDateType = action.payload.dateRangeType;
      state.parameters.selectedReports.holdingsVsTargetVariance.asAtDate = action.payload.dateTo;
    },
    setAsAtDateCurrencyExposureParameter: (state, action: PayloadAction<DateRange>) => {
      state.parameters.selectedReports.currencyExposure.asAtDateType = action.payload.dateRangeType;
      state.parameters.selectedReports.currencyExposure.asAtDate = action.payload.dateTo;
    },
    setDateRangeContributionsAndWithdrawalsParameter: (state, action: PayloadAction<DateRange>) => {
      state.parameters.selectedReports.contributionsAndWithdrawals.dateRangeType = action.payload.dateRangeType;
      state.parameters.selectedReports.contributionsAndWithdrawals.toDate = action.payload.dateTo;
      state.parameters.selectedReports.contributionsAndWithdrawals.fromDate = action.payload.dateFrom;
    },
    setAsAtDateCapitalGainsLossesUnrealisedParameter: (state, action: PayloadAction<DateRange>) => {
      state.parameters.selectedReports.unrealisedCapitalGainsAndLosses.asAtDateType = action.payload.dateRangeType;
      state.parameters.selectedReports.unrealisedCapitalGainsAndLosses.asAtDate = action.payload.dateTo;
    },
    setAsAtDateUnrealisedNonCGTParameter: (state, action: PayloadAction<DateRange>) => {
      state.parameters.selectedReports.unrealisedNonCGT.asAtDateType = action.payload.dateRangeType;
      state.parameters.selectedReports.unrealisedNonCGT.asAtDate = action.payload.dateTo;
    },
    setReportDatePortfolioValuationParameter: (state, action: PayloadAction<DateRange>) => {
      state.parameters.selectedReports.portfolioValuation.asAtDateType = action.payload.dateRangeType;
      state.parameters.selectedReports.portfolioValuation.asAtDate = action.payload.dateTo;
    },
    setDateRangeIncomeReportParameter: (state, action: PayloadAction<DateRange>) => {
      state.parameters.selectedReports.incomeReport.dateRangeType = action.payload.dateRangeType;
      state.parameters.selectedReports.incomeReport.toDate = action.payload.dateTo;
      state.parameters.selectedReports.incomeReport.fromDate = action.payload.dateFrom;
    },
    setDateRangeCashTransactionsParameter: (state, action: PayloadAction<DateRange>) => {
      state.parameters.selectedReports.cashTransactions.dateRangeType = action.payload.dateRangeType;
      state.parameters.selectedReports.cashTransactions.toDate = action.payload.dateTo;
      state.parameters.selectedReports.cashTransactions.fromDate = action.payload.dateFrom;
    },
    setDateRangeExpensesParameter: (state, action: PayloadAction<DateRange>) => {
      state.parameters.selectedReports.expenses.dateRangeType = action.payload.dateRangeType;
      state.parameters.selectedReports.expenses.toDate = action.payload.dateTo;
      state.parameters.selectedReports.expenses.fromDate = action.payload.dateFrom;
    },
    setDateRangeRealisedCapitalGainsAndLossesParameter: (state, action: PayloadAction<DateRange>) => {
      state.parameters.selectedReports.realisedCapitalGainsAndLosses.dateRangeType = action.payload.dateRangeType;
      state.parameters.selectedReports.realisedCapitalGainsAndLosses.toDate = action.payload.dateTo;
      state.parameters.selectedReports.realisedCapitalGainsAndLosses.fromDate = action.payload.dateFrom;
    },
    setDateRangeSecurityPerformanceParameter: (state, action: PayloadAction<DateRange>) => {
      state.parameters.selectedReports.securityPerformance.dateRangeType = action.payload.dateRangeType;
      state.parameters.selectedReports.securityPerformance.toDate = action.payload.dateTo;
      state.parameters.selectedReports.securityPerformance.fromDate = action.payload.dateFrom;
    },
    setDateRangeTransactionHistoryParameter: (state, action: PayloadAction<DateRange>) => {
      state.parameters.selectedReports.transactionHistory.dateRangeType = action.payload.dateRangeType;
      state.parameters.selectedReports.transactionHistory.toDate = action.payload.dateTo;
      state.parameters.selectedReports.transactionHistory.fromDate = action.payload.dateFrom;
    },
    setPortfolioPerformanceHidingOption: (state, action: PayloadAction<HidingOptionsRequest<HidingOptionPortfolioPerformance>>) => {
      state.parameters.selectedReports.portfolioPerformance[action.payload.option] = action.payload.value;
    },
    setAssetAllocationHidingOption: (state, action: PayloadAction<HidingOptionsRequest<HidingOptionAssetAllocation>>) => {
      state.parameters.selectedReports.assetAllocation[action.payload.option] = action.payload.value;
    },
    setSecurityPerformanceHidingOptions: (state, action: PayloadAction<HidingOptionsRequest<HidingOptionSecurityPerformance>>) => {
      state.parameters.selectedReports.securityPerformance[action.payload.option] = action.payload.value;
    },
    setHoldingsVsTargetVarianceHidingOptions: (state, action: PayloadAction<HidingOptionsRequest<HidingOptionHoldingsVsTargetVariance>>) => {
      state.parameters.selectedReports.holdingsVsTargetVariance[action.payload.option] = action.payload.value;
    },
    setCurrencyExposureHidingOptions: (state, action: PayloadAction<HidingOptionsRequest<HidingOptionCurrencyExposure>>) => {
      state.parameters.selectedReports.currencyExposure[action.payload.option] = action.payload.value;
    },
    setCashTransactionsExcludeAudValue: (state, action: PayloadAction<boolean>) => {
      state.parameters.selectedReports.cashTransactions.excludeAudValue = action.payload;
    },
    setReportGeneration: (state, action: PayloadAction<ReportGeneration>) => {
      state.reportGeneration = {
        ...state.reportGeneration,
        isGenerating: action.payload.isGenerating,
        requestId: action.payload.requestId,
        downloadFileName: action.payload.downloadFileName,
        isExpired: action.payload.isExpired,
        isDataFromTriumph: action.payload.isDataFromTriumph,
        isMergeReport: action.payload.isMergeReport,
        key: action.payload.key,
        clientId: action.payload.clientId,
        clientName: action.payload.clientName,
        mergeReportOrder: action.payload.mergeReportOrder,
        dateRange: action.payload.dateRange,
        reportName: action.payload.reportName,
      };
    },
    setSelectedReportingParameter: (state, action: PayloadAction<SavedParameter | null>) => {
      state.parameters.selectedParameter = action.payload;
      if (action.payload === null) {
        const fromDate = state.parameters.inceptionDate.length > 0 ? state.parameters.inceptionDate : SystemMinimumDate;
        state.parameters.includeAppendix = false;
        state.parameters.includeCoverLetter = false;
        state.parameters.includeLogo = true;
        state.parameters.mergeReports = false;
        state.parameters.reportsToSort = [];
        state.parameters.appendix = '';
        state.parameters.coverLetter = '';
        state.parameters.reportFormat = ReportFormat.PDF;
        state.parameters.selectedReports = { ...initialState.parameters.selectedReports };
        state.parameters.selectedReports.assetClassPerformance = {
          ...initialState.parameters.selectedReports.assetClassPerformance,
          dateRangeType: DatePickerNames.InceptionToDate,
          fromDate: fromDate,
        };
        state.parameters.selectedReports.cashTransactions = {
          ...initialState.parameters.selectedReports.cashTransactions,
          dateRangeType: DatePickerNames.InceptionToDate,
          fromDate: fromDate,
        };
        state.parameters.selectedReports.contributionsAndWithdrawals = {
          ...initialState.parameters.selectedReports.contributionsAndWithdrawals,
          dateRangeType: DatePickerNames.InceptionToDate,
          fromDate: fromDate,
        };
        state.parameters.selectedReports.expenses = {
          ...initialState.parameters.selectedReports.expenses,
          dateRangeType: DatePickerNames.InceptionToDate,
          fromDate: fromDate,
        };
        state.parameters.selectedReports.incomeReport = {
          ...initialState.parameters.selectedReports.incomeReport,
          dateRangeType: DatePickerNames.InceptionToDate,
          fromDate: fromDate,
        };
        state.parameters.selectedReports.taxInvoice = {
          ...initialState.parameters.selectedReports.taxInvoice,
          dateRangeType: DatePickerNames.InceptionToDate,
          fromDate: fromDate,
        };
        state.parameters.selectedReports.portfolioPerformance = {
          ...initialState.parameters.selectedReports.portfolioPerformance,
          dateRangeType: DatePickerNames.InceptionToDate,
          fromDate: fromDate,
        };
        state.parameters.selectedReports.portfolioSnapshot = {
          ...initialState.parameters.selectedReports.portfolioSnapshot,
          dateRangeType: DatePickerNames.InceptionToDate,
          fromDate: fromDate,
        };
        state.parameters.selectedReports.securityContribution = {
          ...initialState.parameters.selectedReports.securityContribution,
          dateRangeType: DatePickerNames.InceptionToDate,
          fromDate: fromDate,
        };
        state.parameters.selectedReports.securityPerformance = {
          ...initialState.parameters.selectedReports.securityPerformance,
          dateRangeType: DatePickerNames.InceptionToDate,
          fromDate: fromDate,
        };
        state.parameters.selectedReports.realisedCapitalGainsAndLosses = {
          ...initialState.parameters.selectedReports.realisedCapitalGainsAndLosses,
          dateRangeType: DatePickerNames.InceptionToDate,
          fromDate: fromDate,
        };
        state.parameters.selectedReports.uXchangeTaxStatement = {
          ...initialState.parameters.selectedReports.uXchangeTaxStatement,
          fromDate: DateTime.fromObject({ year: DateTime.now().year - 1, month: 7, day: 1 }).toISODate(),
          toDate: DateTime.fromObject({ year: DateTime.now().year, month: 6, day: 30 }).toISODate(),
          dateRangeType: DatePickerNames.LastFinancialYear,
        };
        state.parameters.selectedReports.taxSummary = {
          ...initialState.parameters.selectedReports.taxSummary,
          fromDate: DateTime.fromObject({ year: DateTime.now().year - 1, month: 7, day: 1 }).toISODate(),
          toDate: DateTime.fromObject({ year: DateTime.now().year, month: 6, day: 30 }).toISODate(),
          dateRangeType: DatePickerNames.LastFinancialYear,
        };
        state.parameters.selectedReports.investmentIncomeTaxComponent = {
          ...initialState.parameters.selectedReports.investmentIncomeTaxComponent,
          fromDate: DateTime.fromObject({ year: DateTime.now().year - 1, month: 7, day: 1 }).toISODate(),
          toDate: DateTime.fromObject({ year: DateTime.now().year, month: 6, day: 30 }).toISODate(),
          dateRangeType: DatePickerNames.LastFinancialYear,
        };
        state.parameters.selectedReports.transactionHistory = {
          ...initialState.parameters.selectedReports.transactionHistory,
          dateRangeType: DatePickerNames.InceptionToDate,
          fromDate: fromDate,
        };
      }
    },
    setReportingParameter: (state, action: PayloadAction<ReportingParameter>) => {
      state.parameters.reportingParameter = action.payload;
    },
    setGroupConsolidatedOption: (state, action: PayloadAction<UpdateGroupConsolidatedOption>) => {
      const report = state.parameters.selectedReports[action.payload.report];
      report.groupOption = action.payload.option;
    },
    setReportFormat: (state, action: PayloadAction<ReportFormat>) => {
      state.parameters.includeCoverLetter = initialState.parameters.includeCoverLetter;
      state.parameters.includeAppendix = initialState.parameters.includeAppendix;
      state.parameters.reportFormat = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(downloadReport.fulfilled, (state, action: PayloadAction<string>) => {
      if (action.payload) {
        state.reportResult = null;
        state.reportGeneration.key = '';
        state.reportGeneration.isGenerated = true;
        state.reportGeneration.isGenerating = false;
      }
    });
    builder.addCase(fetchClientAccount.fulfilled, (state, action: PayloadAction<MainDetails>) => {
      const inceptionDate = action.payload.inceptionDate ?? '';
      state.parameters.inceptionDate = inceptionDate;
      state.parameters.clientName = action.payload.name ?? '';
      state.parameters.clientSubTypeId = action.payload.subTypeId ?? -1;

      if (inceptionDate.length > 0) {
        state.parameters.dates.dateFrom = inceptionDate;
        state.parameters.selectedReports.assetClassPerformance.fromDate = inceptionDate;
        state.parameters.selectedReports.assetClassPerformance.dateRangeType = DatePickerNames.InceptionToDate;
        state.parameters.selectedReports.incomeReport.fromDate = inceptionDate;
        state.parameters.selectedReports.incomeReport.dateRangeType = DatePickerNames.InceptionToDate;
        state.parameters.selectedReports.portfolioSnapshot.fromDate = inceptionDate;
        state.parameters.selectedReports.portfolioSnapshot.dateRangeType = DatePickerNames.InceptionToDate;
        state.parameters.selectedReports.transactionHistory.fromDate = inceptionDate;
        state.parameters.selectedReports.transactionHistory.dateRangeType = DatePickerNames.InceptionToDate;
        state.parameters.selectedReports.cashTransactions.fromDate = inceptionDate;
        state.parameters.selectedReports.cashTransactions.dateRangeType = DatePickerNames.InceptionToDate;
        state.parameters.selectedReports.expenses.fromDate = inceptionDate;
        state.parameters.selectedReports.expenses.dateRangeType = DatePickerNames.InceptionToDate;
        state.parameters.selectedReports.realisedCapitalGainsAndLosses.fromDate = inceptionDate;
        state.parameters.selectedReports.realisedCapitalGainsAndLosses.dateRangeType = DatePickerNames.InceptionToDate;
        state.parameters.selectedReports.securityPerformance.fromDate = inceptionDate;
        state.parameters.selectedReports.securityPerformance.dateRangeType = DatePickerNames.InceptionToDate;
        state.parameters.selectedReports.contributionsAndWithdrawals.fromDate = inceptionDate;
        state.parameters.selectedReports.contributionsAndWithdrawals.dateRangeType = DatePickerNames.InceptionToDate;
        state.parameters.selectedReports.taxInvoice.fromDate = inceptionDate;
        state.parameters.selectedReports.taxInvoice.dateRangeType = DatePickerNames.InceptionToDate;
        state.parameters.selectedReports.portfolioPerformance.fromDate = inceptionDate;
        state.parameters.selectedReports.portfolioPerformance.dateRangeType = DatePickerNames.InceptionToDate;
        state.parameters.selectedReports.securityContribution.fromDate = inceptionDate;
        state.parameters.selectedReports.securityContribution.dateRangeType = DatePickerNames.InceptionToDate;
        state.parameters.selectedReports.assetClassPerformance.multiPeriods = DefaultAvailablePeriods.map((period) => {
          return { periodType: period.periodType.name, periodValue: period.periodValue } as PeriodBaseRequest;
        });
      }
    });
    builder.addCase(initiateReportGeneration.pending, (state) => {
      state.reportGeneration.isGenerating = false;
      state.reportGeneration.isGenerated = false;
    });
    builder.addCase(saveCoverPageFile.fulfilled, (state, action: PayloadAction<TempFileRef>) => {
      state.parameters.includeCoverLetter = true;
      state.parameters.coverLetter = action.payload.fileRef;
    });
    builder.addCase(saveCoverPageFile.rejected, (state) => {
      state.parameters.includeCoverLetter = false;
      state.parameters.coverLetter = undefined;
    });
    builder.addCase(saveAppendixFile.fulfilled, (state, action: PayloadAction<TempFileRef>) => {
      state.parameters.includeAppendix = true;
      state.parameters.appendix = action.payload.fileRef;
    });
    builder.addCase(saveAppendixFile.rejected, (state) => {
      state.parameters.includeAppendix = false;
      state.parameters.appendix = undefined;
    });
    builder.addCase(initiateReportGeneration.fulfilled, (state, action: PayloadAction<InitiateReportRequestModel>) => {
      if (action.payload) {
        state.reportGeneration.requestId = action.payload.id;
        state.reportGeneration.downloadFileName = action.payload.downloadFileName;
        state.reportGeneration.isGenerating = true;
        state.reportGeneration.isGenerated = false;
      }
    });
    builder.addCase(getCGTRecordsForClient.fulfilled, (state, action: PayloadAction<CGTRecord[]>) => {
      if (action.payload) {
        state.clientFinancialYears = action.payload;
        state.clientFinancialYears = [blankcgtRecord];
      }
    });
    builder.addCase(getReportStatus.fulfilled, (state, action: PayloadAction<ReportGeneration>) => {
      if (action.payload) {
        state.reportGeneration.isExpired = action.payload.isExpired;
        state.reportGeneration.key = action.payload.key;
        if (action.payload.key !== '' && action.payload.key !== null) state.reportGeneration.isGenerating = false;
      }
    });
    builder.addCase(fetchReportParametersList.fulfilled, (state, action: PayloadAction<SavedParameter[]>) => {
      state.savedParametersList = action.payload.filter(
        (param: SavedParameter, index: number, self: SavedParameter[]) => index === self.findIndex((p) => p.id === param.id)
      );
      if (state.parameters.selectedParameter === undefined) return;
      if (action.payload.filter((x) => x.id === state.parameters.selectedParameter?.id).length === 0) {
        state.parameters.selectedParameter = undefined;
      }
    });
    builder.addCase(getReportTemplate.fulfilled, (state, action: PayloadAction<ReportParameterTemplateViewModel>) => {
      updateReportParameters(state, action);

      state.parameters.reportingParameter = {
        visibleToAfsl: action.payload.visibleToAfsl,
        afslId: action.payload.afslId,
        afslName: action.payload.afslName,
        parameterName: action.payload.parameterName ?? '',
        kind: action.payload.kind,
      };
    });
    builder.addCase(getReportParametersById.fulfilled, (state, action: PayloadAction<InitiateReportRequest>) => {
      updateReportParameters(state, action);
    });
  },
});
